/**
 * ReaderPage - 閱讀器頁面
 *
 * 重構後使用 reader/ 目錄下的元件:
 * - ReaderHeader: 頂部導航 (返回、標題、設定按鈕)
 * - ReaderSettings: 設定面板
 * - ReaderFooter: 底部導航
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Loading } from '../components/Loading';
import { TapHintOverlay } from '../components/TapHintOverlay';
import { TTSControls } from '../components/TTSControls';
import { ReaderHeader, ReaderSettings, ReaderFooter } from '../components/reader';
import { useReaderSettings, getThemeStyles, FONT_FAMILY_MAP } from '../hooks/useReaderSettings';
import { useDevice } from '../hooks/useDevice';
import { useViewportScroll } from '../hooks/useViewportScroll';
import { useTapZones } from '../hooks/useTapZones';
import { useSwipeNavigation } from '../hooks/useSwipeNavigation';
import type { ChapterContent, ChapterNav, Novel } from '../services/types';
import { normalizeContent } from '../utils/content-normalizer';

export function ReaderPage() {
  const { id: novelId, chapterId } = useParams<{ id: string; chapterId: string }>();
  const navigate = useNavigate();

  // 基本狀態
  const [chapter, setChapter] = useState<ChapterContent | null>(null);
  const [novel, setNovel] = useState<Novel | null>(null);
  const [nav, setNav] = useState<ChapterNav | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI 狀態
  const [showUI, setShowUI] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showTTS, setShowTTS] = useState(false);

  // Refs
  const isProgrammaticScroll = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Hooks
  const { isDesktop, isTouchDevice } = useDevice();
  const settings = useReaderSettings();
  const {
    theme,
    fontFamily,
    fontSize,
    lineHeight,
    paragraphSpacing,
    tapLayout,
    tapNavigationEnabled,
    swipeNavigationEnabled,
    showTapHint,
    dismissTapHint,
  } = settings;
  const themeStyles = getThemeStyles(theme);
  const isDark = theme === 'dark';

  // 內容正規化
  const { paragraphs: normalizedParagraphs, hasIndent } = useMemo(
    () => normalizeContent(chapter?.content || ''),
    [chapter?.content]
  );

  // 滾動控制
  const { scrollToNextPage, scrollToPrevPage, scrollProgress } = useViewportScroll({
    fontSize,
    lineHeight,
    headerHeight: showUI ? 56 : 0,
    footerHeight: showUI ? 48 : 0,
  });

  // 章節導航
  const goToPrevChapter = useCallback(() => {
    if (nav?.prev && novelId) {
      navigate(`/novels/${novelId}/read/${nav.prev.id}`);
    }
  }, [nav?.prev, novelId, navigate]);

  const goToNextChapter = useCallback(() => {
    if (nav?.next && novelId) {
      navigate(`/novels/${novelId}/read/${nav.next.id}`);
    }
  }, [nav?.next, novelId, navigate]);

  // 程式翻頁標記
  const markProgrammaticScroll = useCallback(() => {
    isProgrammaticScroll.current = true;
    setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 500);
  }, []);

  // 點擊區域處理
  const { handleTap } = useTapZones({
    enabled: tapNavigationEnabled && !showSettings,
    layout: tapLayout,
    onPrev: () => {
      markProgrammaticScroll();
      if (!scrollToPrevPage()) goToPrevChapter();
    },
    onMenu: () => {
      setShowUI((prev) => !prev);
      if (showSettings) setShowSettings(false);
    },
    onNext: () => {
      markProgrammaticScroll();
      if (!scrollToNextPage()) goToNextChapter();
    },
  });

  // 手勢滑動翻頁
  useSwipeNavigation({
    enabled: isTouchDevice && swipeNavigationEnabled && !showSettings,
    threshold: 60,
    onSwipeLeft: () => {
      markProgrammaticScroll();
      if (!scrollToNextPage()) goToNextChapter();
    },
    onSwipeRight: () => {
      markProgrammaticScroll();
      if (!scrollToPrevPage()) goToPrevChapter();
    },
  });

  // 鍵盤導航
  useEffect(() => {
    if (!isDesktop) return;

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      switch (e.key) {
        case ' ':
        case 'ArrowDown':
        case 'PageDown':
          e.preventDefault();
          markProgrammaticScroll();
          if (!scrollToNextPage()) goToNextChapter();
          break;
        case 'ArrowUp':
        case 'PageUp':
          e.preventDefault();
          markProgrammaticScroll();
          if (!scrollToPrevPage()) goToPrevChapter();
          break;
        case 'ArrowLeft':
          goToPrevChapter();
          break;
        case 'ArrowRight':
          goToNextChapter();
          break;
        case 'f':
        case 'F':
          setShowUI((prev) => !prev);
          if (showSettings) setShowSettings(false);
          break;
        case 'Escape':
          setShowUI(true);
          if (showSettings) setShowSettings(false);
          break;
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [
    isDesktop,
    scrollToNextPage,
    scrollToPrevPage,
    goToNextChapter,
    goToPrevChapter,
    markProgrammaticScroll,
    showSettings,
  ]);

  // 載入章節
  const loadChapter = useCallback(async () => {
    if (!chapterId || !novelId) return;

    setLoading(true);
    setError(null);

    try {
      const [chapterData, navData, novelData] = await Promise.all([
        api.getChapter(chapterId),
        api.getChapterNav(chapterId),
        api.getNovel(novelId),
      ]);

      setChapter(chapterData);
      setNav(navData);
      setNovel(novelData);
      api.markChapterRead(chapterId);
      window.scrollTo(0, 0);
      setShowUI(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load chapter');
    } finally {
      setLoading(false);
    }
  }, [chapterId, novelId]);

  useEffect(() => {
    if (chapterId && novelId) loadChapter();
  }, [chapterId, novelId, loadChapter]);

  // Toggle handlers
  const handleToggleTTS = useCallback(() => {
    setShowTTS(!showTTS);
    if (showSettings) setShowSettings(false);
  }, [showTTS, showSettings]);

  const handleToggleSettings = useCallback(() => {
    setShowSettings(!showSettings);
    if (showTTS) setShowTTS(false);
  }, [showSettings, showTTS]);

  // Loading & Error
  if (loading) return <Loading text="載入章節中..." />;

  if (error || !chapter) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error || 'Chapter not found'}</p>
        <Link
          to={novelId ? `/novels/${novelId}` : '/novels'}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 inline-block"
        >
          返回
        </Link>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen transition-colors duration-300"
      style={{ backgroundColor: themeStyles.background }}
      onClick={handleTap}
    >
      {/* 頂部導航 */}
      <ReaderHeader
        novelId={novelId}
        novelTitle={novel?.title || null}
        chapterIndex={chapter.chapter_index}
        showUI={showUI}
        theme={theme}
        themeStyles={themeStyles}
        scrollProgress={scrollProgress}
        onToggleTTS={handleToggleTTS}
        onToggleSettings={handleToggleSettings}
      />

      {/* 設定面板 */}

      {showSettings && showUI && (
        <div
          className="fixed top-[56px] left-0 right-0 z-40"
          style={{
            backgroundColor: theme === 'dark' ? 'rgba(26,26,26,0.95)' : 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <ReaderSettings
            {...settings}
            isTouchDevice={isTouchDevice}
            isDesktop={isDesktop}
            themeStyles={themeStyles}
          />
        </div>
      )}

      {/* TTS 面板 */}
      {showTTS && showUI && chapter && (
        <div
          className="fixed top-[56px] left-0 right-0 z-40 border-t px-4 py-4"
          style={{
            backgroundColor: theme === 'dark' ? 'rgba(42,42,42,0.98)' : 'rgba(255,255,255,0.98)',
            borderColor: theme === 'dark' ? '#444' : '#e5e7eb',
            backdropFilter: 'blur(10px)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="max-w-3xl mx-auto">
            <TTSControls
              content={chapter.content}
              themeStyles={themeStyles}
              isDark={isDark}
              onParagraphChange={(index) => {
                const paragraphs = contentRef.current?.querySelectorAll('[data-paragraph-index]');
                const targetParagraph = paragraphs?.[index];
                if (targetParagraph) {
                  targetParagraph.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }}
            />
          </div>
        </div>
      )}

      {/* 主內容 */}
      <article
        className="max-w-3xl mx-auto px-4 py-8"
        style={{
          color: themeStyles.color,
          fontSize: `${fontSize}px`,
          lineHeight: lineHeight,
          fontFamily: FONT_FAMILY_MAP[fontFamily],
          paddingTop: showUI ? '80px' : '32px',
          paddingBottom: showUI ? '80px' : '32px',
        }}
      >
        <h1 className="text-xl font-bold mb-8 text-center">{chapter.title}</h1>

        <div className="reader-content" ref={contentRef}>
          {normalizedParagraphs.map((paragraph, index) => (
            <p
              key={index}
              data-paragraph-index={index}
              style={{
                marginBottom: `${paragraphSpacing}em`,
                textIndent: hasIndent ? '0' : '2em',
              }}
            >
              {paragraph}
            </p>
          ))}
        </div>

        {/* 章節結尾 */}
        <div
          className="mt-12 pt-8 border-t text-center"
          style={{ borderColor: themeStyles.color + '20' }}
        >
          {nav?.next ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNextChapter();
              }}
              className="px-6 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 active:bg-primary-800 transition-colors"
            >
              下一章：{nav.next.title}
            </button>
          ) : (
            <p style={{ color: themeStyles.color, opacity: 0.5 }}>已是最後一章</p>
          )}
        </div>
      </article>

      {/* 底部導航 */}
      <ReaderFooter
        novelId={novelId}
        nav={nav}
        showUI={showUI}
        theme={theme}
        themeStyles={themeStyles}
        scrollProgress={scrollProgress}
        onPrevChapter={goToPrevChapter}
        onNextChapter={goToNextChapter}
      />

      {/* 提示與覆蓋層 */}
      {showTapHint && <TapHintOverlay onDismiss={dismissTapHint} layout={tapLayout} />}
    </div>
  );
}
